import { memo, useCallback, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { cn } from '@air/utils-tailwind';

import { PrivateWorkspaceAgentGuidedVariantFlow } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantFlow';
import { useWorkspaceAgentGuidedVariantFlowStore } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/guided-variants/PrivateWorkspaceAgentGuidedVariantProvider';
import { pinWorkspaceAgentScrollToBottom } from '~/components/v2/components/workspace/workspace-agent-panel/utils/pinWorkspaceAgentScrollToBottom';
import { WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';

type PrivateWorkspaceAgentMessageListGuidedVariantFlowProps = {
  className?: string;
};

/** Single mount for the Make edits and variants wizard (empty, onboarding, and mid-thread). */
export const PrivateWorkspaceAgentMessageListGuidedVariantFlow = memo(
  ({ className }: PrivateWorkspaceAgentMessageListGuidedVariantFlowProps) => {
    const closeGuidedVariantFlow = useWorkspaceAgentGuidedVariantFlowStore((state) => state.closeGuidedVariantFlow);
    const guidedVariantFlowInstanceId = useWorkspaceAgentGuidedVariantFlowStore(
      (state) => state.guidedVariantFlowInstanceId,
    );
    const isGuidedVariantFlowOpen = useWorkspaceAgentGuidedVariantFlowStore((state) => state.isGuidedVariantFlowOpen);
    const rootRef = useRef<HTMLDivElement>(null);
    const pinChatToBottom = useCallback(() => {
      const container = rootRef.current?.closest(`[${WORKSPACE_AGENT_PANEL_CHAT_SCROLL_ATTRIBUTE}]`);

      if (!(container instanceof HTMLElement)) {
        return;
      }

      pinWorkspaceAgentScrollToBottom(container, 'instant');
    }, []);

    useIsomorphicLayoutEffect(() => {
      if (!isGuidedVariantFlowOpen) {
        return;
      }

      pinChatToBottom();
    }, [guidedVariantFlowInstanceId, isGuidedVariantFlowOpen, pinChatToBottom]);

    if (!isGuidedVariantFlowOpen) {
      return null;
    }

    return (
      <div className={cn('pb-3', className)} ref={rootRef}>
        <PrivateWorkspaceAgentGuidedVariantFlow
          key={guidedVariantFlowInstanceId}
          onClose={closeGuidedVariantFlow}
          onStepChange={pinChatToBottom}
        />
      </div>
    );
  },
);

PrivateWorkspaceAgentMessageListGuidedVariantFlow.displayName = 'PrivateWorkspaceAgentMessageListGuidedVariantFlow';
